import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { toOccurredAt } from './ClinicalEventEntry';
import type { ClinicalSubject } from './types';
import type { ProposedEntry } from './PrenatalEntry';
import type { CourseTask, CourseTaskSchedule } from './postpartumCourseApi';
export function PostpartumCourseTasks({
  tasks,
  subjects,
  timeZone,
  disabled,
  onSave,
  onSchedule,
  onReviewSource,
  assignees = [],
}: {
  tasks: CourseTask[];
  subjects: ClinicalSubject[];
  timeZone: string;
  disabled?: boolean;
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  onSchedule?: (input: CourseTaskSchedule) => Promise<unknown>;
  assignees?: { id: string; name: string }[];
  onReviewSource?: (eventId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Postpartum follow-up and review tasks</CardTitle>
        <CardDescription>
          Due windows are reviewed practice reminders. A task becomes a booked appointment only after an explicit
          scheduling action succeeds.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!tasks.length && (
          <p>
            No current follow-up tasks are available. Review practice scheduling settings and source discharge evidence.
          </p>
        )}
        {tasks.map((task) => (
          <TaskRow
            key={`${task.id}:${task.source_event_id}:${task.configuration_version}`}
            task={task}
            subject={subjects.find((s) => s.id === task.subject_id)?.label ?? task.subject_id}
            timeZone={timeZone}
            disabled={disabled}
            onSave={onSave}
            onSchedule={onSchedule}
            onReviewSource={onReviewSource}
            assignees={assignees}
          />
        ))}
      </CardContent>
    </Card>
  );
}
function TaskRow({
  task,
  subject,
  timeZone,
  disabled,
  onSave,
  onSchedule,
  onReviewSource,
  assignees,
}: {
  task: CourseTask;
  subject: string;
  timeZone: string;
  disabled?: boolean;
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  onSchedule?: (input: CourseTaskSchedule) => Promise<unknown>;
  assignees: { id: string; name: string }[];
  onReviewSource?: (eventId: string) => void;
}) {
  const [time, setTime] = useState(''),
    [assignee, setAssignee] = useState('UNASSIGNED'),
    [reviewed, setReviewed] = useState(false),
    [action, setAction] = useState(''),
    [note, setNote] = useState(''),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  async function schedule() {
    setMessage('');
    try {
      if (!reviewed || !onSchedule) throw Error('Review the proposed appointment while connected before scheduling.');
      setBusy(true);
      await onSchedule({
        confirmed: true,
        task_id: task.id,
        source_event_id: task.source_event_id,
        configuration_version: task.configuration_version,
        scheduled_at: toOccurredAt(time, timeZone),
        assigned_to: assignee === 'UNASSIGNED' ? null : assignee,
      });
      setMessage('Scheduling accepted. Refresh to review the booked encounter.');
      setReviewed(false);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Scheduling failed; values retained.');
    } finally {
      setBusy(false);
    }
  }
  async function complete() {
    setMessage('');
    try {
      if (!action || !note.trim()) throw Error('Choose a review outcome and enter supporting details.');
      setBusy(true);
      await onSave(
        [
          {
            event_type: 'CARE.TASK',
            schema_version: 2,
            subject_id: task.subject_id,
            payload: { task_id: task.id, source_event_id: task.source_event_id, status: action, note: note.trim() },
          },
        ],
        new Date().toISOString(),
      );
      setMessage('Task review saved locally; server confirmation may be pending.');
      setAction('');
      setNote('');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Task review could not be saved.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="flex flex-col gap-3 rounded border p-4" aria-label={task.label}>
      <h4 className="font-semibold">
        {task.label} · {subject}
      </h4>
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">{task.status}</Badge>
        <Badge variant="secondary">{task.booking ? 'Booked appointment' : 'Due task — not booked'}</Badge>
      </div>
      <p>
        Window: {new Date(task.due_from).toLocaleString(undefined, { timeZone })}–
        {new Date(task.due_until).toLocaleString(undefined, { timeZone })}
      </p>
      {task.booking ? (
        <p>
          Booked {new Date(task.booking.scheduled_at).toLocaleString(undefined, { timeZone })}
          {task.booking.source_changed ? ' · Source evidence changed; review this appointment.' : ''}
        </p>
      ) : (
        task.type === 'VISIT' && (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={`${task.id}-time`}>Proposed appointment ({timeZone})</FieldLabel>
              <Input
                id={`${task.id}-time`}
                type="datetime-local"
                className="min-h-11"
                value={time}
                onChange={(e) => {
                  setTime(e.target.value);
                  setReviewed(false);
                }}
                disabled={disabled || busy}
              />
            </Field>
            <Field>
              <FieldLabel>Assigned provider</FieldLabel>
              <Select
                value={assignee}
                onValueChange={(value) => {
                  setAssignee(value);
                  setReviewed(false);
                }}
                disabled={disabled || busy}>
                <SelectTrigger className="min-h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                    {assignees.map((a) => (
                      <SelectItem value={a.id} key={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field orientation="horizontal">
              <Checkbox
                id={`${task.id}-review`}
                checked={reviewed}
                onCheckedChange={(v) => setReviewed(v === true)}
                disabled={disabled || busy}
              />
              <FieldLabel htmlFor={`${task.id}-review`}>I reviewed the subject, time and assignment.</FieldLabel>
            </Field>
            <Button
              type="button"
              className="min-h-11"
              disabled={disabled || busy || !onSchedule || !reviewed}
              onClick={schedule}>
              Book reviewed appointment
            </Button>
            {!onSchedule && (
              <p className="text-sm text-muted-foreground">
                Connect to book an appointment; this due task remains available offline.
              </p>
            )}
          </FieldGroup>
        )
      )}
      {['SIGN', 'COSIGN'].includes(task.type) ? (
        <Button
          type="button"
          className="min-h-11"
          variant="outline"
          disabled={!onReviewSource}
          onClick={() => onReviewSource?.(task.source_event_id)}>
          Open original entry for {task.type === 'COSIGN' ? 'co-signature' : 'signature'}
        </Button>
      ) : (
        <>
          <FieldGroup>
            <Field>
              <FieldLabel>Task review outcome</FieldLabel>
              <ToggleGroup
                type="single"
                variant="outline"
                value={action}
                onValueChange={setAction}
                disabled={disabled || busy}
                className="justify-start flex-wrap"
                aria-label={`${task.label} outcome`}>
                {[
                  ['COMPLETE', 'Completed'],
                  ['DEFERRED', 'Deferred'],
                  ['NOT_APPLICABLE', 'Not applicable'],
                ].map(([value, label]) => (
                  <ToggleGroupItem key={value} value={value} className="min-h-11">
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${task.id}-note`}>Review details / reason</FieldLabel>
              <Textarea
                id={`${task.id}-note`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={disabled || busy}
              />
            </Field>
          </FieldGroup>
          <Button
            type="button"
            variant="outline"
            className="min-h-11 self-start"
            disabled={disabled || busy}
            onClick={complete}>
            Save task review
          </Button>
        </>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
